import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Query } from "@tanstack/react-query";

import { db } from "@/lib/db";
import { isStorageDegraded, markStorageDegraded } from "@/lib/storageCapability";

/**
 * Server data the dashboard needs while offline: the punch dropdowns
 * (cost codes, customers) and the recent-entries view that drives the
 * clocked-in/out state. Everything else refetches normally when online.
 */
const PERSISTED_QUERY_KEYS = new Set(["cost-codes", "customers", "recent-entries", "recent-entries-count"]);

export const QUERY_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const queryPersister = createAsyncStoragePersister({
  key: "rq-cache",
  storage: {
    getItem: async (key: string) => {
      if (isStorageDegraded()) return null;
      try {
        const row = await db.kv.get(key);
        return typeof row?.value === "string" ? row.value : null;
      } catch (e) {
        markStorageDegraded(`queryPersister.getItem: ${e instanceof Error ? e.message : String(e)}`);
        return null;
      }
    },
    setItem: async (key: string, value: string) => {
      if (isStorageDegraded()) return;
      try {
        await db.kv.put({ key, value });
      } catch (e) {
        markStorageDegraded(`queryPersister.setItem: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    removeItem: async (key: string) => {
      if (isStorageDegraded()) return;
      try {
        await db.kv.delete(key);
      } catch {
        /* ignore */
      }
    },
  },
});

export function shouldPersistQuery(query: Query): boolean {
  return query.state.status === "success" && PERSISTED_QUERY_KEYS.has(String(query.queryKey[0]));
}
